import { useState, type FormEvent } from 'react';
import { Button } from '../../shared/ui/Button.js';
import { signIn, SignInError } from '../../shared/auth/session.js';

const SECONDS_PER_MINUTE = 60;

interface SignInFormProps {
  /** Prefilled and read-only when the dialog reopens an expired session. */
  readonly email?: string;
  readonly onSignedIn: (email: string) => void;
}

/** E-mail and password; one message for any wrong pair (functional §9.2: "Identifiants incorrects"). */
export function SignInForm({ email: fixedEmail, onSignedIn }: SignInFormProps) {
  const [email, setEmail] = useState(fixedEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotten, setForgotten] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      onSignedIn(email);
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate={false}>
      <label className="login__field">
        <span>Adresse e-mail</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={fixedEmail !== undefined}
          required
          autoComplete="username"
        />
      </label>

      <label className="login__field">
        <span>Mot de passe</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          autoFocus={fixedEmail !== undefined}
        />
      </label>

      {error && (
        <p className="login__error" role="alert">
          {error}
        </p>
      )}

      <Button variant="primary" size="md" type="submit" disabled={busy} className="login__submit">
        {busy ? 'Connexion…' : 'Se connecter'}
      </Button>

      <button type="button" className="login__forgot" onClick={() => setForgotten((value) => !value)}>
        Mot de passe oublié ?
      </button>
      {forgotten && (
        <p className="login__hint">
          Adressez-vous à l'administration centrale d'Ederest : elle vous remettra un lien de
          réinitialisation, valable 24 heures.
        </p>
      )}
    </form>
  );
}

function messageFor(cause: unknown): string {
  if (!(cause instanceof SignInError)) return 'Connexion impossible pour le moment. Réessayez.';
  if (cause.code === 'INVALID_CREDENTIALS' || cause.code === 'VALIDATION_FAILED')
    return 'Identifiants incorrects.';
  if (cause.code === 'ACCOUNT_LOCKED' || cause.code === 'RATE_LIMITED') {
    const minutes = Math.max(
      1,
      Math.ceil((cause.retryAfterSeconds ?? SECONDS_PER_MINUTE) / SECONDS_PER_MINUTE),
    );
    return `Trop de tentatives. Réessayez dans ${minutes} minute${minutes > 1 ? 's' : ''}.`;
  }
  return 'Connexion impossible pour le moment. Réessayez.';
}
